import { motion } from "framer-motion";
import { HeartHandshake, Users, Sprout, Sparkles, Truck, House, Palette } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { FEATURES, MILESTONES, ABOUT_IMAGE } from "../data/content";

const ease = [0.22, 1, 0.36, 1];
const ICONS = { heart: HeartHandshake, hands: Users, leaf: Sprout, sparkle: Sparkles };
const MILESTONE_ICONS = { truck: Truck, home: House, sparkle: Sparkles, palette: Palette };

export default function About() {
  return (
    <section id="about" className="relative overflow-hidden bg-sand py-16 sm:py-24 lg:py-32" data-testid="about-section">
      <div className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          {/* Image collage */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_60px_rgba(13,75,86,0.18)]">
              <img
                src={ABOUT_IMAGE}
                alt="Volunteers packing food kits during a community drive"
                className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-[480px]"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-8 -right-2 hidden w-52 overflow-hidden rounded-3xl border-4 border-sand shadow-[0_20px_40px_rgba(13,75,86,0.25)] sm:block lg:-right-8">
              <img
                src="/images/floods-handing.jpg"
                alt="Hope Giver volunteer handing relief supplies during the Punjab floods"
                className="h-60 w-full object-cover"
                loading="lazy"
              />
            </div>
            <motion.div
              className="absolute -top-5 -left-3 rounded-2xl bg-[#0D4B56] px-5 py-3 shadow-xl lg:-left-6"
              initial={{ opacity: 0, y: 16, rotate: -4 }}
              whileInView={{ opacity: 1, y: 0, rotate: -4 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6, ease }}
            >
              <p className="font-script text-2xl font-bold text-[#F3E096]">made with hope</p>
            </motion.div>
          </motion.div>

          {/* Copy + values */}
          <div>
            <SectionHeading
              eyebrow="About Hope Giver"
              title="Helping hands,"
              accent="hopeful hearts."
              sub="Hope Giver is a community initiative committed to helping people in need — through compassion, service and collective action. From flood relief in Punjab to Soch Ke Rang 7.0 at the Gymkhana and a visit to Unique Home — we are just getting started."
            />
            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              {FEATURES.map((f, i) => {
                const Icon = ICONS[f.icon];
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.6, delay: 0.1 * i, ease }}
                    whileHover={{ y: -6 }}
                    className="group rounded-3xl border border-[#0D4B56]/10 bg-white p-6 shadow-[0_10px_30px_rgba(13,75,86,0.06)] transition-shadow duration-300 hover:shadow-[0_20px_40px_rgba(13,75,86,0.12)]"
                    data-testid={`feature-card-${f.title.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E6F0F2] text-[#0D4B56] transition-colors duration-300 group-hover:bg-[#D4AF37] group-hover:text-[#08353D]">
                      <Icon size={24} />
                    </span>
                    <h3 className="mt-4 font-display text-xl font-bold text-[#0F172A]">{f.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#475569]">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Journey band — real milestones only, no invented numbers */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease }}
          className="grain relative mt-24 overflow-hidden rounded-[2.5rem] bg-[#0D4B56] px-6 py-12 sm:px-12 lg:mt-32 lg:py-16"
          data-testid="journey-band"
        >
          <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <p className="relative text-center text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">
            Our journey so far
          </p>
          <div className="relative mt-9 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {MILESTONES.map((m, i) => {
              const Icon = MILESTONE_ICONS[m.icon];
              return (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: 0.12 * i, ease }}
                  data-testid={m.testid}
                  className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-6"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-[#F3E096]">
                      <Icon size={24} />
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.16em] ${
                        m.status === "Done"
                          ? "bg-emerald-400/15 text-emerald-300"
                          : "bg-[#D4AF37]/15 text-[#F3E096]"
                      }`}
                    >
                      {m.status}
                    </span>
                  </div>
                  <h3 className="mt-1 font-display text-xl font-bold text-white">{m.title}</h3>
                  <p className="text-sm leading-relaxed text-white/65">{m.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
